export interface NodeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const GAP = 40;
const MAX_RINGS = 200;

const collides = (candidate: NodeRect, occupied: NodeRect[]) =>
  occupied.some(
    (node) =>
      !(
        candidate.x + candidate.width + GAP <= node.x ||
        node.x + node.width + GAP <= candidate.x ||
        candidate.y + candidate.height + GAP <= node.y ||
        node.y + node.height + GAP <= candidate.y
      )
  );

/**
 * The free spot nearest to `preferred` for a node of `size`: the preferred spot itself when
 * nothing is there, else the closest ring of grid steps around it that clears every node.
 */
export const findFreePosition = (
  preferred: { x: number; y: number },
  size: { width: number; height: number },
  occupied: NodeRect[]
): { x: number; y: number } => {
  const fits = (x: number, y: number) => !collides({ x, y, ...size }, occupied);
  if (fits(preferred.x, preferred.y)) {
    return { x: preferred.x, y: preferred.y };
  }
  const stepX = size.width + GAP;
  const stepY = size.height + GAP;
  for (let ring = 1; ring < MAX_RINGS; ring += 1) {
    let best: { x: number; y: number } | null = null;
    let bestDistance = Infinity;
    for (let dx = -ring; dx <= ring; dx += 1) {
      for (let dy = -ring; dy <= ring; dy += 1) {
        if (Math.abs(dx) !== ring && Math.abs(dy) !== ring) {
          continue;
        }
        const x = preferred.x + dx * stepX;
        const y = preferred.y + dy * stepY;
        const distance = dx * dx + dy * dy;
        if (distance < bestDistance && fits(x, y)) {
          best = { x, y };
          bestDistance = distance;
        }
      }
    }
    if (best) {
      return best;
    }
  }
  return { x: preferred.x, y: preferred.y };
};
